export const GEMINI_INPUT_SAMPLE_RATE = 16000
export const GEMINI_OUTPUT_SAMPLE_RATE = 24000
export const GEMINI_AUDIO_CHANNELS = 1
export const GEMINI_UPLOAD_CHUNK_MS = 40
export const GEMINI_PLAYBACK_STARTUP_BUFFER_MS = 120
export const GEMINI_PLAYBACK_SCHEDULE_LEAD_MS = 20

export interface GeminiAudioFormat {
  mimeType: string
  sampleRate: number
  channels: number
}

export interface GeminiPlaybackScheduleResult {
  playbackStarted: boolean
  bufferedAheadSeconds: number
  underrunCount: number
}

export interface GeminiPlaybackAudioBuffer {
  duration: number
  getChannelData(channel: number): Float32Array
}

export interface GeminiPlaybackAudioSource {
  buffer: GeminiPlaybackAudioBuffer | null
  connect(destination: unknown): void
  start(when?: number): void
}

export interface GeminiPlaybackAudioContextLike {
  currentTime: number
  destination: unknown
  createBuffer(channels: number, length: number, sampleRate: number): GeminiPlaybackAudioBuffer
  createBufferSource(): GeminiPlaybackAudioSource
}

export function parseGeminiMimeSampleRate(
  mimeType?: string,
  fallback = GEMINI_OUTPUT_SAMPLE_RATE
): number {
  if (!mimeType) {
    return fallback
  }

  const match = mimeType.match(/(?:^|[;,])\s*rate=(\d+)/i)
  if (!match) {
    return fallback
  }

  const parsed = Number.parseInt(match[1], 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

export function normalizeGeminiAudioFormat(
  format?: Partial<GeminiAudioFormat>
): GeminiAudioFormat {
  const mimeType = format?.mimeType || `audio/pcm;rate=${GEMINI_OUTPUT_SAMPLE_RATE}`
  return {
    mimeType,
    sampleRate: format?.sampleRate ?? parseGeminiMimeSampleRate(mimeType),
    channels: format?.channels ?? GEMINI_AUDIO_CHANNELS,
  }
}

function clampAudioSample(sample: number): number {
  return Math.max(-1, Math.min(1, sample))
}

function floatToPcm16(sample: number): number {
  const clamped = clampAudioSample(sample)
  return clamped < 0 ? Math.round(clamped * 0x8000) : Math.round(clamped * 0x7fff)
}

export function resampleFloatToPCM16(
  input: Float32Array,
  inputRate: number,
  outputRate = GEMINI_INPUT_SAMPLE_RATE
): Int16Array {
  if (!input.length || inputRate <= 0 || outputRate <= 0) {
    return new Int16Array()
  }

  if (inputRate === outputRate) {
    const output = new Int16Array(input.length)
    for (let index = 0; index < input.length; index += 1) {
      output[index] = floatToPcm16(input[index])
    }
    return output
  }

  const outputLength = Math.max(1, Math.round((input.length * outputRate) / inputRate))
  const output = new Int16Array(outputLength)
  const inputLastIndex = input.length - 1

  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const position = (outputIndex * inputRate) / outputRate
    const leftIndex = Math.min(Math.floor(position), inputLastIndex)
    const rightIndex = Math.min(leftIndex + 1, inputLastIndex)
    const weight = position - leftIndex
    const sample = input[leftIndex] + ((input[rightIndex] - input[leftIndex]) * weight)
    output[outputIndex] = floatToPcm16(sample)
  }

  return output
}

function pcm16ToFloat32(input: Int16Array): Float32Array {
  const output = new Float32Array(input.length)
  for (let index = 0; index < input.length; index += 1) {
    output[index] = input[index] / 32768
  }
  return output
}

function getChunkDurationSeconds(chunk: ArrayBuffer, sampleRate: number, channels: number): number {
  if (sampleRate <= 0 || channels <= 0) {
    return 0
  }

  const sampleCount = new Int16Array(chunk).length / channels
  return sampleCount / sampleRate
}

export class GeminiPcmPlaybackScheduler {
  private audioContext: GeminiPlaybackAudioContextLike
  private format: GeminiAudioFormat
  private startupBufferSeconds: number
  private scheduleLeadSeconds: number
  private pendingChunks: Array<ArrayBuffer> = []
  private pendingDurationSeconds = 0
  private playbackStarted = false
  private nextPlaybackTime = 0
  private underrunCount = 0

  constructor(
    audioContext: GeminiPlaybackAudioContextLike,
    format?: Partial<GeminiAudioFormat>,
    options?: {
      startupBufferMs?: number
      scheduleLeadMs?: number
    }
  ) {
    this.audioContext = audioContext
    this.format = normalizeGeminiAudioFormat(format)
    this.startupBufferSeconds = (options?.startupBufferMs ?? GEMINI_PLAYBACK_STARTUP_BUFFER_MS) / 1000
    this.scheduleLeadSeconds = (options?.scheduleLeadMs ?? GEMINI_PLAYBACK_SCHEDULE_LEAD_MS) / 1000
  }

  setFormat(format?: Partial<GeminiAudioFormat>) {
    this.format = normalizeGeminiAudioFormat(format)
  }

  enqueue(chunk: ArrayBuffer): GeminiPlaybackScheduleResult {
    if (!chunk.byteLength) {
      return {
        playbackStarted: false,
        bufferedAheadSeconds: this.getBufferedAheadSeconds(),
        underrunCount: this.underrunCount,
      }
    }

    if (this.playbackStarted && this.nextPlaybackTime <= this.audioContext.currentTime) {
      this.underrunCount += 1
      this.playbackStarted = false
      this.nextPlaybackTime = 0
      this.pendingChunks = []
      this.pendingDurationSeconds = 0
    }

    this.pendingChunks.push(chunk)
    this.pendingDurationSeconds += getChunkDurationSeconds(
      chunk,
      this.format.sampleRate,
      this.format.channels
    )

    let justStarted = false
    if (!this.playbackStarted && this.pendingDurationSeconds >= this.startupBufferSeconds) {
      this.playbackStarted = true
      this.nextPlaybackTime = this.audioContext.currentTime + this.scheduleLeadSeconds
      justStarted = true
    }

    if (this.playbackStarted) {
      this.flushPendingChunks()
    }

    return {
      playbackStarted: justStarted,
      bufferedAheadSeconds: this.getBufferedAheadSeconds(),
      underrunCount: this.underrunCount,
    }
  }

  reset() {
    this.pendingChunks = []
    this.pendingDurationSeconds = 0
    this.playbackStarted = false
    this.nextPlaybackTime = 0
  }

  getBufferedAheadSeconds(): number {
    if (!this.playbackStarted) {
      return this.pendingDurationSeconds
    }

    return Math.max(0, this.nextPlaybackTime - this.audioContext.currentTime) + this.pendingDurationSeconds
  }

  getUnderrunCount(): number {
    return this.underrunCount
  }

  getNextPlaybackTime(): number {
    return this.nextPlaybackTime
  }

  private flushPendingChunks() {
    while (this.pendingChunks.length > 0) {
      const chunk = this.pendingChunks.shift()
      if (!chunk) {
        continue
      }

      this.scheduleChunk(chunk, this.nextPlaybackTime)
      const duration = getChunkDurationSeconds(chunk, this.format.sampleRate, this.format.channels)
      this.pendingDurationSeconds = Math.max(0, this.pendingDurationSeconds - duration)
      this.nextPlaybackTime += duration
    }
  }

  private scheduleChunk(chunk: ArrayBuffer, startTime: number) {
    const pcm16 = new Int16Array(chunk)
    const float32 = pcm16ToFloat32(pcm16)
    const audioBuffer = this.audioContext.createBuffer(
      this.format.channels,
      float32.length / this.format.channels,
      this.format.sampleRate
    )
    audioBuffer.getChannelData(0).set(float32)

    const source = this.audioContext.createBufferSource()
    source.buffer = audioBuffer
    source.connect(this.audioContext.destination)
    source.start(startTime)
  }
}

/**
 * Peak-weighted RMS of a Float32 mono buffer, mapped to a 0..1 display level.
 *
 * Raw RMS on speech sits low and barely moves a meter, so the result is passed
 * through a cube root to expand the quiet end — this is a visual indicator, not
 * a measurement instrument, and it should react visibly to normal speech.
 */
export function rmsFromFloat32(samples: Float32Array): number {
  if (!samples.length) return 0

  let sum = 0
  for (let i = 0; i < samples.length; i += 1) {
    sum += samples[i] * samples[i]
  }

  const rms = Math.sqrt(sum / samples.length)
  return Math.min(1, Math.cbrt(rms) * 1.35)
}

/**
 * Same as {@link rmsFromFloat32} for a little-endian PCM16 buffer.
 *
 * An odd trailing byte is ignored rather than treated as a sample; chunks are
 * frame-aligned in practice, but a partial frame shouldn't spike the meter.
 */
export function rmsFromPCM16(buffer: ArrayBuffer): number {
  const usableBytes = buffer.byteLength - (buffer.byteLength % 2)
  if (usableBytes <= 0) return 0

  const samples = new Int16Array(buffer, 0, usableBytes / 2)
  let sum = 0
  for (let i = 0; i < samples.length; i += 1) {
    const v = samples[i] / 32768
    sum += v * v
  }

  const rms = Math.sqrt(sum / samples.length)
  return Math.min(1, Math.cbrt(rms) * 1.35)
}

/**
 * Asymmetric smoothing for a level meter: jump to peaks, ease back down.
 *
 * Symmetric smoothing makes speech look mushy and delayed. Attacking fast and
 * releasing slowly is how audio meters have always behaved, and it reads as
 * responsive without flickering between chunks.
 */
export function smoothLevel(previous: number, next: number): number {
  return next > previous
    ? previous + (next - previous) * 0.6
    : previous + (next - previous) * 0.18
}
